import { SVG_ENUM, VALIDATION_ENUM } from "enums";
import { Field, Formik, Form } from "formik";
import { useGetUsersWithShares, useUpdateCompanyParity } from "hooks/companies";
import { useRef, useState, useEffect } from "react";
import "./CompanyTransformationModal.scss";
import { Button, FullscreenModal, Input, Warning, SVG } from "shared";
import { CompanyType } from "types/CompanyTypes";
import { scrollToError } from "utils";
import * as Yup from "yup";

interface CompanyTransformationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  company?: CompanyType | null;
}

const CompanyTransformationModal: React.FC<CompanyTransformationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  company,
}) => {
  const { mutateAsync: updateCompanyParity } = useUpdateCompanyParity();
  const { data: usersWithShares } = useGetUsersWithShares(
    company?.uuid,
    isOpen,
  );
  const formRef = useRef<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSummary, setIsSummary] = useState(false);

  const initialValues = {
    name: company?.name || "",
    parity: null,
  };

  const onSubmit = async (values: { name: string; parity: number | null }) => {
    if (!isSummary) {
      setIsSummary(true);
      return;
    }

    setIsSubmitting(true);
    try {
      if (!company?.uuid) return;
      await updateCompanyParity(
        {
          uuid: company.uuid,
          name: values.name,
          parity: Number(values.parity),
        },
        {
          onSuccess: () => {
            onSuccess && onSuccess();
            onClose();
          },
        },
      );
    } finally {
      scrollToError(formRef.current?.errors);
      setIsSubmitting(false);
    }
  };

  const validation = () =>
    Yup.object().shape({
      name: Yup.string()
        .required(VALIDATION_ENUM.REQUIRED)
        .max(50, VALIDATION_ENUM.MAX_50)
        .min(3, VALIDATION_ENUM.MIN3)
        .matches(
          /^[^\s].*?[^\s]$/,
          VALIDATION_ENUM.CANNOT_START_AND_END_WITH_SPACE,
        ),
      parity: Yup.number().required(VALIDATION_ENUM.REQUIRED),
    });

  useEffect(() => {
    if (!isOpen) {
      setIsSummary(false);
    }
  }, [isOpen]);

  return (
    <FullscreenModal
      isOpen={isOpen}
      onClose={onClose}
      className={`transformation-modal${
        isSummary ? " transformation-modal-summary" : ""
      }`}
    >
      <h2 className="fullscreen-modal__header transformation-modal__header">
        {isSummary ? "Podsumowanie" : "Zmiana rodzaju spółki"}
      </h2>
      <Formik
        initialValues={initialValues}
        onSubmit={async (values: any) => await onSubmit(values)}
        validationSchema={validation()}
        enableReinitialize
        innerRef={formRef}
        validateOnMount={false}
      >
        {({ values, submitForm }) => {
          const parityValue = Number(values.parity) || 0;
          const parityDisplay = values.parity ? values.parity : "-";

          return (
            <Form className="m-width-100 fullscreen-modal__select-margin">
              <div className="transformation-form-content">
                {!isSummary ? (
                  <>
                    <Field
                      type="text"
                      id="name"
                      name="name"
                      label={"Nazwa spółki"}
                      as={Input}
                      maxLength={50}
                    />

                    <Warning
                      text={`Uwaga! Przy zmianie spółki udziałowej na akcyjną operacja jest nieodwracalna!`}
                    />

                    <Field
                      type="number"
                      id="parity"
                      name="parity"
                      label="Parytet"
                      as={Input}
                      maxLength={10}
                      precision={1}
                      inputMode={"decimal"}
                    />
                  </>
                ) : (
                  <div className="transformation-content">
                    <p className="transformation-summary__subtitle">
                      Zmiana spółki udziałowej na akcyjną
                    </p>
                    <Warning
                      text={`Uwaga! Upewnij się, że wszystkie dane są poprawne. Operacja jest nieodwracalna!`}
                    />
                    <div className="transformation-summary">
                      <div className="transformation-summary__row">
                        <span className="transformation-summary__label">
                          Nazwa spółki:
                        </span>
                        <span className="transformation-summary__value">
                          {values.name || "-"}
                        </span>
                      </div>
                      <div className="transformation-summary__row transformation-summary__row--parity">
                        <span className="transformation-summary__label">
                          Parytet wymiany:
                        </span>
                        <span className="transformation-summary__value">
                          {parityDisplay}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className={`transformation-parity`}>
                  <div className="transformation-parity-item">
                    <span className="transformation-parity-label">
                      Liczba udziałów
                    </span>
                    <span className="transformation-parity-value">1</span>
                  </div>
                  <div className="transformation-parity-arrow">
                    <SVG type={SVG_ENUM.ARROW_LONG} />
                  </div>
                  <div className="transformation-parity-item">
                    <span className="transformation-parity-label">
                      Liczba akcji
                    </span>
                    <span className="transformation-parity-value">
                      {parityValue > 0
                        ? Math.max(Math.round(1 * parityValue), 1)
                        : 0}
                    </span>
                  </div>
                </div>

                <div className="transformation-users-section">
                  <h3 className="transformation-users-section__title">
                    Lista klientów posiadających udziały w tej spółce:
                  </h3>
                  {usersWithShares?.length ? (
                    <div className="transformation-users-section__list">
                      {usersWithShares.map((user) => {
                        const parity = parityValue;
                        const sharesCount = Number(user.sharesCount) || 0;
                        let stocksCount = 0;
                        if (parity > 0) {
                          const calculatedStocks = Math.round(
                            sharesCount * parity,
                          );
                          stocksCount = Math.max(calculatedStocks, 1);
                        }

                        return (
                          <div
                            key={user.shareUuid || user.shareId}
                            className={`transformation-users-section__item${
                              isSummary
                                ? " transformation-users-section__item--summary"
                                : ""
                            }`}
                          >
                            <div
                              className={`transformation-users-section__name ${
                                isSummary
                                  ? "transformation-users-section__name--summary"
                                  : ""
                              }`}
                            >
                              {user.name || "-"}
                            </div>
                            <div className="transformation-users-section__row">
                              <span className="transformation-users-section__label">
                                Liczba udziałów:
                              </span>
                              <span className="transformation-users-section__value">
                                {sharesCount}
                              </span>
                              <span className="transformation-users-section__arrow">
                                <SVG type={SVG_ENUM.ARROW_LONG} />
                              </span>
                              <span className="transformation-users-section__label">
                                Liczba akcji:
                              </span>
                              <span className="transformation-users-section__value">
                                {stocksCount}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="transformation-users-section__empty">
                      Brak klientów z udziałami.
                    </div>
                  )}
                </div>
              </div>

              <div className="fullscreen-modal__buttons">
                {!isSummary ? (
                  <>
                    <Button
                      label="Anuluj"
                      onClick={onClose}
                      className="button--stroke button--rounded"
                    />
                    <Button
                      label={"Dalej"}
                      type="button"
                      onClick={async () => {
                        await submitForm().then(() => {
                          if (
                            Object.keys(formRef.current?.errors || {}).length >
                            0
                          ) {
                            scrollToError(formRef.current?.errors);
                          }
                        });
                      }}
                      className=" button--rounded button--black"
                    />
                  </>
                ) : (
                  <>
                    <Button
                      label="Wstecz"
                      onClick={() => {
                        setIsSummary(false);
                      }}
                      className="button--stroke button--rounded"
                    />
                    <Button
                      label={"Potwierdź"}
                      type="button"
                      onClick={async () => {
                        await submitForm();
                      }}
                      className=" button--rounded button--black"
                      isLoading={isSubmitting}
                    />
                  </>
                )}
              </div>
            </Form>
          );
        }}
      </Formik>
    </FullscreenModal>
  );
};

export default CompanyTransformationModal;
